import {
  addDays, addHours, differenceInCalendarDays, eachDayOfInterval, eachMonthOfInterval,
  eachWeekOfInterval,
  endOfDay, format, min, parseISO, startOfMonth, startOfWeek, subDays, subMonths, subWeeks,
} from "date-fns"
import { formatInTimeZone } from "date-fns-tz"
import { EventActivity } from "sharedTypes"
import { EventPhase } from "./phase"

export type ActivityInterval = "hourly" | "daily" | "weekly" | "monthly"

// Period keys are the event's local times, "yyyy-MM-ddTHH:mm:ss".
const DATE = "yyyy-MM-dd"

export const API_INTERVAL: Record<ActivityInterval, string> = {
  hourly: "hour", daily: "day", weekly: "week", monthly: "month",
}

// Stepped in UTC so the browser's DST gaps can't skip an event hour.
const eachHourBetween = (from: string, to: string) => {
  const start = new Date(`${from}T00:00:00Z`)
  const hours = (differenceInCalendarDays(parseISO(to), parseISO(from)) + 1) * 24

  return Array.from({ length: hours }, (_, hour) => (
    addHours(start, hour).toISOString().slice(0, 19)
  ))
}

export const periodsBetween = (interval: ActivityInterval, from: string, to: string) => {
  if (interval === "hourly") return eachHourBetween(from, to)

  const range = { start: parseISO(from), end: endOfDay(parseISO(to)) }
  const dates = {
    daily: () => eachDayOfInterval(range),
    weekly: () => eachWeekOfInterval(range, { weekStartsOn: 1 }),
    monthly: () => eachMonthOfInterval(range),
  }[interval]()

  return dates.map((date) => `${format(date, DATE)}T00:00:00`)
}

export const fillPeriods = (periods: string[], values: Record<string, number>) => (
  periods.map((period) => values[period] ?? 0)
)

// For values keyed by plain date, "yyyy-MM-dd".
export const fillDays = (periods: string[], values: Record<string, number>) => (
  periods.map((period) => values[period.slice(0, 10)] ?? 0)
)

export type PeriodTotals = {
  registrations: Omit<EventActivity["registrations"][number], "period">
  gross: number
  net: number
  checkIns: number
  // Check-ins per session, keyed by check-in location id ("null" without one).
  sessions?: Record<string, number>
}

export const periodTotals = (
  {
    registrations, sales, checkIns, checkInsByLocation,
  }: EventActivity,
  period: string,
): PeriodTotals => {
  const inPeriod = (row: { period: string }) => row.period === period
  const sum = <T extends { period: string }>(rows: T[], value: (row: T) => number) => (
    rows.filter(inPeriod).reduce((total, row) => total + value(row), 0)
  )
  const types = ["registration", "rsvp", "ticket", "waitlist", "survey"] as const

  return {
    registrations: Object.fromEntries(
      types.map((type) => [type, sum(registrations, (row) => row[type])]),
    ) as PeriodTotals["registrations"],
    gross: sum(sales, ({ gross }) => gross),
    net: sum(sales, ({ net }) => net),
    checkIns: sum(checkIns, ({ count }) => count),
    sessions: checkInsByLocation?.filter(inPeriod).reduce<Record<string, number>>(
      (totals, { checkInLocationId: id, count }) => (
        { ...totals, [String(id)]: (totals[String(id)] ?? 0) + count }
      ),
      {},
    ),
  }
}

export const defaultInterval = (phase: EventPhase): ActivityInterval => (
  { "pre-event": "daily", "day-of": "hourly", "post-event": "daily" } as const
)[phase]

// After the event, Daily ends today until the event's last day would drop out of the 30 days
// shown, then stays on the 30 days that start with that day; Hourly shows the last day.
// Weekly and Monthly keep running to today.
export const rangeAnchor = (
  phase: EventPhase, interval: ActivityInterval, now: Date, end: Date,
) => {
  if (phase !== "post-event") return now
  if (interval === "daily") return min([now, addDays(end, 29)])
  if (interval === "hourly") return min([now, end])

  return now
}

export const todayIn = (timezone: string, now = new Date()) => formatInTimeZone(now, timezone, DATE)

export const defaultRange = (interval: ActivityInterval, now: Date, timezone: string) => {
  const today = parseISO(todayIn(timezone, now))
  const from = {
    hourly: today,
    daily: subDays(today, 29),
    weekly: startOfWeek(subWeeks(today, 11), { weekStartsOn: 1 }),
    monthly: startOfMonth(subMonths(today, 11)),
  }[interval]

  return { from: format(from, DATE), to: format(today, DATE) }
}

// Without a start, from the first activity on.
export type ActivityRange = { from?: string, to: string }

export const rangeLabel = ({ from, to }: ActivityRange, today = new Date()) => {
  const otherYear = [from, to]
    .some((date) => date && parseISO(date).getFullYear() !== today.getFullYear())
  const label = (date: string) => format(parseISO(date), otherYear ? "MMM d, yyyy" : "MMM d")

  if (!from) return "All time"
  if (from === to) return label(to)

  return `${label(from)} – ${label(to)}`
}

const HOURLY_MAX_DAYS = 7

export const fitsHourly = ({ from, to }: ActivityRange) => (
  Boolean(from)
    && differenceInCalendarDays(parseISO(to), parseISO(from as string)) < HOURLY_MAX_DAYS
)

export const lastDays = (days: number, today: string): ActivityRange => (
  { from: format(subDays(parseISO(today), days - 1), DATE), to: today }
)

// A weekly or monthly range starts on the period's first day, so the first bar is whole.
export const snapRange = (interval: ActivityInterval, range: ActivityRange): ActivityRange => {
  if (!range.from || (interval !== "weekly" && interval !== "monthly")) return range

  const from = parseISO(range.from)
  const start = interval === "weekly" ? startOfWeek(from, { weekStartsOn: 1 }) : startOfMonth(from)

  return { ...range, from: format(start, DATE) }
}

// Parsing the hour could land in the browser's DST gap.
export const periodLabel = (interval: ActivityInterval, period: string) => (
  interval === "hourly"
    ? format(new Date(2000, 0, 1, Number(period.slice(11, 13))), "ha")
    : format(parseISO(period), {
      daily: "MMM d", weekly: "MMM d", monthly: "MMM yyyy",
    }[interval])
)
